import React, { useState, useEffect } from 'react';
import { 
  BellIcon, 
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import intranetApi from '../../services/intranetApi';
import { getIconComponent } from '../../utils/iconMapping';
import { renderContentWithMentions } from '../../utils/mentions';

const RequiredReading = () => {
  const [requiredPosts, setRequiredPosts] = useState([]);
  const [completedPosts, setCompletedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [acknowledging, setAcknowledging] = useState({});
  const [expandedPosts, setExpandedPosts] = useState(new Set());

  useEffect(() => {
    fetchRequiredReading();
  }, []);

  const fetchRequiredReading = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await intranetApi.posts.getRequiredReading();
      const data = response.data;

      setRequiredPosts(data.required_posts || []);
      setCompletedPosts(data.completed_posts || []);
    } catch (err) {
      console.error('Error fetching required reading:', err);
      setError('Failed to load required reading. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (postId) => {
    try {
      setAcknowledging(prev => ({ ...prev, [postId]: true }));
      
      await intranetApi.posts.acknowledgeRequiredReading(postId);
      
      // Move post from required to completed
      const acknowledgedPost = requiredPosts.find(post => post.id === postId);
      if (acknowledgedPost) {
        setRequiredPosts(prev => prev.filter(post => post.id !== postId));
        setCompletedPosts(prev => [
          { ...acknowledgedPost, acknowledged_at: new Date().toISOString() },
          ...prev
        ]);
      }
    } catch (err) {
      console.error('Error acknowledging post:', err);
      // You could show a toast notification here
    } finally {
      setAcknowledging(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleReadMore = (postId) => {
    setExpandedPosts(prev => new Set([...prev, postId]));
  };

  const handleShowLess = (postId) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev);
      newSet.delete(postId);
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysOverdue = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = now - created;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyColor = (daysOld) => {
    if (daysOld <= 3) return 'text-green-600 bg-green-100';
    if (daysOld <= 7) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const renderPostCard = (post, isPending = true) => {
    const daysOld = getDaysOverdue(post.created_at);
    const urgencyColor = getUrgencyColor(daysOld);

    return (
      <div key={post.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                  {post.author_name ? post.author_name.charAt(0).toUpperCase() : 'U'}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{post.author_name || 'Unknown User'}</p>
                <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
              </div>
            </div>
            
            {isPending ? (
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${urgencyColor}`}>
                  <ClockIcon className="h-3 w-3 mr-1" />
                  {daysOld === 1 ? '1 day old' : `${daysOld} days old`}
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <BellIcon className="h-3 w-3 mr-1" />
                  Required
                </span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircleSolidIcon className="h-3 w-3 mr-1" />
                  Completed
                </span>
                {post.acknowledged_at && (
                  <span className="text-xs text-gray-500">
                    {formatDate(post.acknowledged_at)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Category */}
          {post.category_label && (
            <div className="mt-3">
              <span 
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: post.category_color || '#6B7280' }}
              >
                {(() => {
                  const IconComponent = getIconComponent(post.category_icon, 'category');
                  return <IconComponent className="h-3 w-3 mr-1" />;
                })()}
                {post.category_label}
              </span>
            </div>
          )}

          {/* Content */}
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h4>
            <div className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none">
              {expandedPosts.has(post.id) ? (
                <>
                  <div className="rich-text-content">
                    {renderContentWithMentions(post.content)}
                  </div>
                  <button 
                    onClick={() => handleShowLess(post.id)}
                    className="text-indigo-600 hover:text-indigo-500 ml-1 mt-2 inline-block"
                  >
                    Show less
                  </button>
                </>
              ) : post.content.length > 200 ? (
                <>
                  <div className="rich-text-content">
                    {renderContentWithMentions(post.content.substring(0, 200))}
                  </div>
                  ...
                  <button 
                    onClick={() => handleReadMore(post.id)}
                    className="text-indigo-600 hover:text-indigo-500 ml-1 mt-2 inline-block"
                  >
                    Read more
                  </button>
                </>
              ) : (
                <div className="rich-text-content">
                  {renderContentWithMentions(post.content)}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <EyeIcon className="h-4 w-4 mr-1" />
                {post.view_count || 0} views
              </div>
              {post.comment_count > 0 && (
                <div className="flex items-center">
                  <span>{post.comment_count} comments</span>
                </div>
              )}
            </div>
            
            {isPending && (
              <button
                onClick={() => handleAcknowledge(post.id)}
                disabled={acknowledging[post.id]}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {acknowledging[post.id] ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Acknowledging...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Mark as Read
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Required Reading</h2>
            <p className="mt-1 text-sm text-gray-500">
              Important posts that require your acknowledgment
            </p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchRequiredReading} 
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Required Reading</h2>
          <p className="mt-1 text-sm text-gray-500">
            Important posts that require your acknowledgment
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BellIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">{requiredPosts.length}</p>
              <p className="text-sm text-gray-500">Pending Reading</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleSolidIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">{completedPosts.length}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-gray-900">
                {requiredPosts.filter(post => getDaysOverdue(post.created_at) > 7).length}
              </p>
              <p className="text-sm text-gray-500">Overdue (7+ days)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BellIcon className="h-5 w-5 inline mr-2" />
            Pending ({requiredPosts.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'completed'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CheckCircleIcon className="h-5 w-5 inline mr-2" />
            Completed ({completedPosts.length})
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'pending' && (
          <>
            {requiredPosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircleSolidIcon className="h-12 w-12 text-green-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h3>
                  <p className="text-gray-500">
                    You have no pending required reading. Great job staying up to date!
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {requiredPosts.map(post => renderPostCard(post, true))}
              </div>
            )}
          </>
        )}

        {activeTab === 'completed' && (
          <>
            {completedPosts.length === 0 ? (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
                <div className="max-w-md mx-auto">
                  <div className="h-24 w-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircleIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Completed Reading</h3>
                  <p className="text-gray-500">
                    Your completed required reading will appear here once you start acknowledging posts.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {completedPosts.map(post => renderPostCard(post, false))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RequiredReading; 